import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    MagnifyingGlassIcon, 
    AdjustmentsHorizontalIcon,
    DocumentArrowDownIcon,
    UsersIcon,
    TagIcon,
    CalendarIcon,
    CurrencyDollarIcon,
    ChartBarIcon,
    EyeIcon,
    XMarkIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import Modal from './Modal';
import CampaignManualAdjustments from './CampaignManualAdjustments';

/**
 * Admin Kampanya Takip Paneli
 * Kullanıcı kampanya kullanımını takip etmek ve yönetmek için admin arayüzü
 */
const AdminCampaignTrackingPanel = () => {
    const [campaignUsageData, setCampaignUsageData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        campaign_type: 'all',
        status: 'all',
        date_range: '30d'
    });
    const [statistics, setStatistics] = useState({
        totalUsers: 0,
        totalCampaigns: 0,
        totalSavings: 0,
        avgSavingsPerUser: 0
    });
    const { token } = useAuth();

    // Kampanya türü görüntü isimleri
    const getCampaignTypeDisplayName = (type) => {
        const displayNames = {
            'flash_sale': 'Flash Sale',
            'category': 'Kategori İndirimi',
            'cart': 'Sepet Kampanyası',
            'coupon': 'Kupon İndirimi',
            'package': 'Paket Kampanyası',
            'special_day': 'Özel Gün Kampanyası'
        };
        return displayNames[type] || type;
    };

    // Badge renkleri
    const getCampaignBadgeColor = (type) => {
        const colors = {
            'flash_sale': 'bg-red-100 text-red-800',
            'category': 'bg-blue-100 text-blue-800',
            'cart': 'bg-green-100 text-green-800',
            'coupon': 'bg-purple-100 text-purple-800',
            'package': 'bg-yellow-100 text-yellow-800',
            'special_day': 'bg-pink-100 text-pink-800'
        };
        return colors[type] || 'bg-gray-100 text-gray-800';
    };

    // Status renkleri
    const getStatusBadgeColor = (status) => {
        const colors = {
            'active': 'bg-green-100 text-green-800',
            'refunded': 'bg-red-100 text-red-800',
            'partial_refund': 'bg-yellow-100 text-yellow-800',
            'expired': 'bg-gray-100 text-gray-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Kullanıcı kampanya verilerini getir
    const fetchCampaignUsageData = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/admin/campaign-usage/reports-new', {
                headers: { Authorization: `Bearer ${token}` },
                params: filters
            });

            if (response.success) {
                setCampaignUsageData(response.data.usageData || []);
                setStatistics(response.data.statistics || {});
            }
        } catch (error) {
            console.error('Kampanya kullanım verileri alınırken hata:', error);
            toast.error('Kampanya verileri yüklenemedi');
            // Fallback veri yapısı
            setCampaignUsageData([]);
            setStatistics({
                totalUsers: 0,
                totalCampaigns: 0,
                totalSavings: 0,
                avgSavingsPerUser: 0
            });
        } finally {
            setLoading(false);
        }
    };

    // Kullanıcı detay verilerini getir
    const fetchUserCampaignDetails = async (userId) => {
        try {
            const response = await api.get(`/api/admin/campaign-usage/user/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // API yanıtı kontrolü - hem success field'ı hem de direkt data kontrolü
            if (response.data.success && response.data.data) {
                // Normal API yanıtı: {success: true, data: {...}}
                setSelectedUser(response.data.data);
                setShowUserModal(true);
            } else if (response.data.user_name || response.data.user_email) {
                // Direkt data yanıtı: {user_name: ..., user_email: ...}
                setSelectedUser(response.data);
                setShowUserModal(true);
            } else {
                toast.error('Kullanıcı detayları alınamadı');
            }
        } catch (error) {
            console.error('Kullanıcı kampanya detayları alınırken hata:', error);
            toast.error('Kullanıcı detayları yüklenemedi');
        }
    };

    // Rapor indirme
    const downloadReport = async (format = 'csv') => {
        try {
            // Önce mevcut verileri al
            const response = await api.get('/api/admin/campaign-usage/reports-new', {
                headers: { Authorization: `Bearer ${token}` },
                params: filters
            });

            // API yanıt formatını kontrol et
            let campaignUsageData = [];
            
            if (response.data.success && response.data.data && response.data.data.usageData) {
                // Normal format: {success: true, data: {usageData: [...]}}
                campaignUsageData = response.data.data.usageData;
            } else if (response.data.usageData) {
                // Direkt format: {usageData: [...]}
                campaignUsageData = response.data.usageData;
            } else if (Array.isArray(response.data)) {
                // Array format: [...]
                campaignUsageData = response.data;
            } else {
                console.error('Beklenmeyen API yanıt formatı:', response.data);
                throw new Error('Veri formatı tanınmıyor');
            }

            if (campaignUsageData.length === 0) {
                toast.warning('İndirilecek veri bulunamadı');
                return;
            }

            // Veriyi export formatına dönüştür
            const exportData = campaignUsageData.map(usage => ({
                'Kampanya ID': usage.id || '',
                'Kullanıcı Email': usage.user_email || 'Bilinmeyen',
                'Kullanıcı Adı': usage.user_name || 'Bilinmeyen',
                'Kampanya Adı': usage.campaign_name || 'Bilinmeyen',
                'Kampanya Türü': getCampaignTypeDisplayName(usage.campaign_type),
                'Sipariş No': usage.order_number || 'Bilinmeyen',
                'Orijinal İndirim': `₺${(usage.original_discount_amount || 0).toFixed(2)}`,
                'Kalan İndirim': `₺${(usage.remaining_discount_amount || 0).toFixed(2)}`,
                'İade Tutarı': `₺${(usage.refund_amount || 0).toFixed(2)}`,
                'Durum': getStatusDisplayName(usage.status),
                'Kullanım Tarihi': new Date(usage.usage_date).toLocaleDateString('tr-TR'),
                'Oluşturma Tarihi': new Date(usage.created_at).toLocaleDateString('tr-TR')
            }));

            let content, mimeType, extension;

            if (format === 'csv') {
                content = convertToCSV(exportData);
                mimeType = 'text/csv;charset=utf-8';
                extension = 'csv';
            } else if (format === 'excel') {
                content = convertToExcelXLSX(exportData);
                mimeType = 'application/vnd.ms-excel';
                extension = 'xls';
            } else {
                throw new Error('Desteklenmeyen format');
            }

            // Dosyayı indir
            const blob = new Blob(['\uFEFF' + content], { type: mimeType });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `kampanya-raporu-${new Date().toISOString().split('T')[0]}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.success(`${format.toUpperCase()} raporu başarıyla indirildi (${exportData.length} kayıt)`);
        } catch (error) {
            console.error('Rapor indirme hatası:', error);
            toast.error('Rapor indirilemedi: ' + (error.message || 'Bilinmeyen hata'));
        }
    };

    // Yardımcı fonksiyonlar
    const getStatusDisplayName = (status) => {
        const displayNames = {
            'active': 'Aktif',
            'refunded': 'İade Edildi',
            'partial_refund': 'Kısmi İade',
            'expired': 'Süresi Doldu'
        };
        return displayNames[status] || status;
    };

    const convertToCSV = (data) => {
        if (!data.length) return '';
        
        const headers = Object.keys(data[0]);
        const csvRows = [];
        
        // Header satırı
        csvRows.push(headers.map(header => `"${header}"`).join(','));
        
        // Veri satırları
        data.forEach(row => {
            const values = headers.map(header => {
                const value = row[header] || '';
                return `"${String(value).replace(/"/g, '""')}"`;
            });
            csvRows.push(values.join(','));
        });
        
        return csvRows.join('\n');
    };

    const convertToExcel = (data) => {
        if (!data.length) return '';
        
        const headers = Object.keys(data[0]);
        const excelRows = [];
        
        // Header satırı
        excelRows.push(headers.join('\t'));
        
        // Veri satırları
        data.forEach(row => {
            const values = headers.map(header => {
                const value = row[header] || '';
                return String(value).replace(/\t/g, ' ');
            });
            excelRows.push(values.join('\t'));
        });
        
        return excelRows.join('\n');
    };

    const convertToExcelXLSX = (data) => {
        if (!data.length) return '';
        
        const headers = Object.keys(data[0]);
        
        // Basit Excel uyumlu HTML formatı
        let htmlContent = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>Kampanya Raporu</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
<style>
table { border-collapse: collapse; width: 100%; font-family: Arial; }
th { background-color: #4472C4; color: white; font-weight: bold; border: 1px solid #000; padding: 8px; text-align: center; }
td { border: 1px solid #ccc; padding: 6px; text-align: left; }
tr:nth-child(even) { background-color: #f9f9f9; }
.number { text-align: right; }
.status-active { background-color: #d4edda; color: #155724; font-weight: bold; }
.status-refunded { background-color: #f8d7da; color: #721c24; font-weight: bold; }
.status-partial { background-color: #fff3cd; color: #856404; font-weight: bold; }
.status-expired { background-color: #e2e3e5; color: #383d41; font-weight: bold; }
</style>
</head>
<body>
<h2>📊 Kampanya Kullanım Raporu</h2>
<p>Oluşturma Tarihi: ${new Date().toLocaleDateString('tr-TR')} ${new Date().toLocaleTimeString('tr-TR')}<br>Toplam Kayıt: ${data.length} adet</p>
<table>
<thead><tr>`;

        // Header satırları
        headers.forEach(header => {
            htmlContent += `<th>${escapeXml(header)}</th>`;
        });
        
        htmlContent += `</tr></thead><tbody>`;

        // Veri satırları
        data.forEach(row => {
            htmlContent += '<tr>';
            headers.forEach(header => {
                const value = row[header] || '';
                const isNumber = /^₺[\d,]+\.?\d*$/.test(value);
                
                let cellClass = '';
                if (header === 'Durum') {
                    if (value === 'Aktif') cellClass = 'status-active';
                    else if (value === 'İade Edildi') cellClass = 'status-refunded';
                    else if (value === 'Kısmi İade') cellClass = 'status-partial';
                    else if (value === 'Süresi Doldu') cellClass = 'status-expired';
                } else if (isNumber) {
                    cellClass = 'number';
                }
                
                htmlContent += `<td${cellClass ? ` class="${cellClass}"` : ''}>${escapeXml(value)}</td>`;
            });
            htmlContent += '</tr>';
        });

        htmlContent += `</tbody></table>
<div style="margin-top: 20px; font-size: 10pt; color: #666;">
<p><strong>Notlar:</strong></p>
<ul>
<li>Bu rapor Excel'de filtrelenebilir</li>
<li>Para birimleri sağa hizalı</li>
<li>Durum sütunları renkli</li>
<li>Tarih: ${new Date().toLocaleDateString('tr-TR')}</li>
</ul>
</div>
</body>
</html>`;

        return htmlContent;
    };

    // XML escape fonksiyonu
    const escapeXml = (str) => {
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    };

    useEffect(() => {
        fetchCampaignUsageData();
    }, [filters]);

    const StatCard = ({ title, value, icon: Icon, color = 'blue', suffix = '' }) => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-gradient-to-br from-${color}-50 to-${color}-100 border border-${color}-200 rounded-xl p-6 hover:shadow-lg transition-all`}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className={`text-${color}-600 text-sm font-medium`}>{title}</p>
                    <p className={`text-${color}-900 text-2xl font-bold`}>{value}{suffix}</p>
                </div>
                <div className={`w-12 h-12 bg-${color}-500 rounded-lg flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
            </div>
        </motion.div>
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Kampanya Takip Merkezi</h2>
                    <p className="text-gray-600">Kullanıcı kampanya kullanımlarını izleyin ve yönetin</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setShowInfoModal(true)}
                        className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        title="Sayfa Bilgileri"
                    >
                        <InformationCircleIcon className="w-4 h-4 mr-2" />
                        Bilgi
                    </button>
                    <button
                        onClick={() => downloadReport('csv')}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                        CSV İndir
                    </button>
                    <button
                        onClick={() => downloadReport('excel')}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                        Excel İndir
                    </button>
                </div>
            </div>

            {/* İstatistik Kartları */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    title="Toplam Kullanıcı"
                    value={statistics.totalUsers || 0}
                    icon={UsersIcon}
                    color="blue"
                />
                <StatCard
                    title="Toplam Kampanya"
                    value={statistics.totalCampaigns || 0}
                    icon={TagIcon}
                    color="green"
                />
                <StatCard
                    title="Toplam Tasarruf"
                    value={`₺${(statistics.totalSavings || 0).toFixed(2)}`}
                    icon={CurrencyDollarIcon}
                    color="purple"
                />
                <StatCard
                    title="Ortalama Tasarruf"
                    value={`₺${(statistics.avgSavingsPerUser || 0).toFixed(2)}`}
                    icon={ChartBarIcon}
                    color="orange"
                />
            </div>

            {/* Filtreler */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex items-center gap-4 mb-4">
                    <AdjustmentsHorizontalIcon className="w-5 h-5 text-gray-500" />
                    <h3 className="font-semibold text-gray-900">Filtreler</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Arama</label>
                        <div className="relative">
                            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                placeholder="Kullanıcı adı, email..."
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Kampanya Türü</label>
                        <select
                            value={filters.campaign_type}
                            onChange={(e) => setFilters(prev => ({ ...prev, campaign_type: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">Tümü</option>
                            <option value="flash_sale">Flash Sale</option>
                            <option value="category">Kategori İndirimi</option>
                            <option value="cart">Sepet Kampanyası</option>
                            <option value="coupon">Kupon</option>
                            <option value="package">Paket Kampanyası</option>
                            <option value="special_day">Özel Gün</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Durum</label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="all">Tümü</option>
                            <option value="active">Aktif</option>
                            <option value="refunded">İade Edildi</option>
                            <option value="partial_refund">Kısmi İade</option>
                            <option value="expired">Süresi Doldu</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tarih Aralığı</label>
                        <select
                            value={filters.date_range}
                            onChange={(e) => setFilters(prev => ({ ...prev, date_range: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="7d">Son 7 Gün</option>
                            <option value="30d">Son 30 Gün</option>
                            <option value="90d">Son 90 Gün</option>
                            <option value="1y">Son 1 Yıl</option>
                            <option value="all">Tümü</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Kampanya Kullanım Tablosu */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">Kampanya Kullanım Geçmişi</h3>
                </div>
                
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">Yükleniyor...</span>
                    </div>
                ) : campaignUsageData.length === 0 ? (
                    <div className="text-center py-12">
                        <ExclamationTriangleIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">Henüz kampanya kullanım verisi bulunmuyor</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Kullanıcı
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Kampanya
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tür
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tasarruf
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Durum
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tarih
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        İşlemler
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {campaignUsageData.map((usage, index) => (
                                    <motion.tr
                                        key={usage.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="hover:bg-gray-50"
                                    >
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                                                    {usage.user_name?.charAt(0) || 'U'}
                                                </div>
                                                <div className="ml-3">
                                                    <p className="text-sm font-medium text-gray-900">{usage.user_name || 'Bilinmeyen'}</p>
                                                    <p className="text-sm text-gray-500">{usage.user_email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm font-medium text-gray-900">{usage.campaign_name}</p>
                                            <p className="text-sm text-gray-500">Sipariş #{usage.order_number}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCampaignBadgeColor(usage.campaign_type)}`}>
                                                {getCampaignTypeDisplayName(usage.campaign_type)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm font-medium text-green-600">₺{usage.original_discount_amount?.toFixed(2)}</p>
                                            {usage.remaining_discount_amount !== usage.original_discount_amount && (
                                                <p className="text-xs text-gray-500">Kalan: ₺{usage.remaining_discount_amount?.toFixed(2)}</p>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(usage.status)}`}>
                                                {usage.status === 'active' ? 'Aktif' :
                                                 usage.status === 'refunded' ? 'İade Edildi' :
                                                 usage.status === 'partial_refund' ? 'Kısmi İade' :
                                                 usage.status === 'expired' ? 'Süresi Doldu' : usage.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(usage.usage_date).toLocaleDateString('tr-TR')}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center space-x-2">
                                                <button
                                                    onClick={() => fetchUserCampaignDetails(usage.user_id)}
                                                    className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                                                >
                                                    <EyeIcon className="w-4 h-4 mr-1" />
                                                    Detay
                                                </button>
                                                <CampaignManualAdjustments 
                                                    campaignUsageId={usage.id}
                                                    onAdjustmentComplete={() => fetchCampaignUsageData()}
                                                />
                                            </div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Kullanıcı Detay Modal */}
            <AnimatePresence>
                {showUserModal && selectedUser && (
                    <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)}>
                        <div className="bg-white rounded-xl max-w-4xl w-full mx-auto max-h-[80vh] overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="text-xl font-bold">{selectedUser.user_name} - Kampanya Detayları</h3>
                                        <p className="text-blue-100">{selectedUser.user_email}</p>
                                    </div>
                                    <button
                                        onClick={() => setShowUserModal(false)}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-6 overflow-y-auto max-h-96">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                                        <p className="text-sm text-green-600">Toplam Tasarruf</p>
                                        <p className="text-2xl font-bold text-green-800">₺{selectedUser.total_savings?.toFixed(2) || '0.00'}</p>
                                    </div>
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                                        <p className="text-sm text-blue-600">Kullanılan Kampanya</p>
                                        <p className="text-2xl font-bold text-blue-800">{selectedUser.total_campaigns_used || 0}</p>
                                    </div>
                                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                                        <p className="text-sm text-purple-600">Sipariş Sayısı</p>
                                        <p className="text-2xl font-bold text-purple-800">{selectedUser.total_orders_with_campaigns || 0}</p>
                                    </div>
                                </div>

                                {selectedUser.campaign_history && selectedUser.campaign_history.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-4">Kampanya Geçmişi</h4>
                                        <div className="space-y-3">
                                            {selectedUser.campaign_history.map((history, index) => (
                                                <div key={index} className="bg-gray-50 rounded-lg p-4">
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <p className="font-medium text-gray-900">{history.campaign_name}</p>
                                                            <p className="text-sm text-gray-500">
                                                                {getCampaignTypeDisplayName(history.campaign_type)} • 
                                                                {new Date(history.usage_date).toLocaleDateString('tr-TR')}
                                                            </p>
                                                        </div>
                                                        <div className="text-right">
                                                            <p className="font-semibold text-green-600">₺{history.original_discount_amount?.toFixed(2)}</p>
                                                            <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadgeColor(history.status)}`}>
                                                                {history.status}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>

            {/* Bilgi Modal */}
            <AnimatePresence>
                {showInfoModal && (
                    <Modal isOpen={showInfoModal} onClose={() => setShowInfoModal(false)}>
                        <div className="bg-white rounded-xl max-w-4xl w-full mx-auto max-h-[90vh] overflow-hidden">
                            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 text-white">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <InformationCircleIcon className="h-8 w-8" />
                                        <div>
                                            <h3 className="text-xl font-bold">Kampanya Takip Merkezi - Kullanım Kılavuzu</h3>
                                            <p className="text-indigo-100">Bu sayfanın tüm özelliklerini öğrenin</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setShowInfoModal(false)}
                                        className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>
                            </div>
                            
                            <div className="p-6 overflow-y-auto max-h-[70vh]">
                                <div className="space-y-6">
                                    {/* Ana Amaç */}
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                        <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                                            <TagIcon className="h-5 w-5 mr-2" />
                                            Bu Sayfa Ne İşe Yarar?
                                        </h4>
                                        <p className="text-blue-800 text-sm">
                                            Kampanya Takip Merkezi, müşterilerin kampanya kullanımlarını izlemenizi, 
                                            yönetmenizi ve gerektiğinde manuel düzeltmeler yapmanızı sağlar. 
                                            Tüm kampanya aktivitelerini tek yerden kontrol edebilirsiniz.
                                        </p>
                                    </div>

                                    {/* İstatistik Kartları */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <ChartBarIcon className="h-5 w-5 mr-2 text-purple-500" />
                                            İstatistik Kartları
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <span className="font-medium text-blue-600">Toplam Kullanıcı:</span>
                                                <p className="text-gray-700">Kampanya kullanan toplam müşteri sayısı</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <span className="font-medium text-green-600">Toplam Kampanya:</span>
                                                <p className="text-gray-700">Kullanılan toplam kampanya adedi</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <span className="font-medium text-purple-600">Toplam Tasarruf:</span>
                                                <p className="text-gray-700">Müşterilerin toplam tasarruf miktarı</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <span className="font-medium text-orange-600">Ortalama Tasarruf:</span>
                                                <p className="text-gray-700">Kullanıcı başına ortalama tasarruf</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Filtreler */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2 text-green-500" />
                                            Filtreleme Seçenekleri
                                        </h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex items-start space-x-2">
                                                <span className="font-medium text-gray-700 min-w-[100px]">Arama:</span>
                                                <span className="text-gray-600">Kullanıcı adı veya email ile arama yapın</span>
                                            </div>
                                            <div className="flex items-start space-x-2">
                                                <span className="font-medium text-gray-700 min-w-[100px]">Kampanya Türü:</span>
                                                <span className="text-gray-600">Flash Sale, Kategori, Sepet, Kupon, Paket, Özel Gün</span>
                                            </div>
                                            <div className="flex items-start space-x-2">
                                                <span className="font-medium text-gray-700 min-w-[100px]">Durum:</span>
                                                <span className="text-gray-600">Aktif, İade Edildi, Kısmi İade, Süresi Doldu</span>
                                            </div>
                                            <div className="flex items-start space-x-2">
                                                <span className="font-medium text-gray-700 min-w-[100px]">Tarih:</span>
                                                <span className="text-gray-600">Son 7 gün, 30 gün, 90 gün, 1 yıl veya tümü</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* İşlem Butonları */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <EyeIcon className="h-5 w-5 mr-2 text-blue-500" />
                                            İşlem Butonları
                                        </h4>
                                        <div className="space-y-3 text-sm">
                                            <div className="bg-blue-50 p-3 rounded-lg">
                                                <span className="font-medium text-blue-700">Detay Butonu:</span>
                                                <p className="text-blue-600 mt-1">
                                                    Kullanıcının tüm kampanya geçmişini, toplam tasarrufunu ve 
                                                    sipariş sayısını görüntüler. Kullanıcı bazlı özet bilgiler sunar.
                                                </p>
                                            </div>
                                            <div className="bg-orange-50 p-3 rounded-lg">
                                                <span className="font-medium text-orange-700">Düzelt Butonu:</span>
                                                <p className="text-orange-600 mt-1">
                                                    Manuel kampanya düzeltmesi yapar. İndirim tutarını, durumu veya 
                                                    diğer değerleri değiştirebilirsiniz. Tüm değişiklikler kayıt altına alınır.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Manuel Düzeltme */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                                            Manuel Düzeltme Sistemi
                                        </h4>
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h5 className="font-medium text-yellow-800 mb-2">Ne Zaman Kullanılır?</h5>
                                            <ul className="text-yellow-700 text-sm space-y-1 list-disc list-inside">
                                                <li>Müşteri şikayeti durumunda indirim tutarını düzeltmek</li>
                                                <li>Sistem hatası nedeniyle yanlış hesaplanan kampanyaları düzeltmek</li>
                                                <li>İade işlemlerinde kampanya tutarlarını ayarlamak</li>
                                                <li>Kampanya durumunu manuel olarak değiştirmek</li>
                                                <li>Özel durumlar için indirim miktarını güncellemek</li>
                                            </ul>
                                        </div>
                                    </div>

                                    {/* Rapor İndirme */}
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                                            <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
                                            Rapor İndirme
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                            <div className="bg-green-50 p-3 rounded-lg">
                                                <span className="font-medium text-green-700">CSV İndir:</span>
                                                <p className="text-green-600 mt-1">Excel'de açılabilir, analiz için uygun format</p>
                                            </div>
                                            <div className="bg-blue-50 p-3 rounded-lg">
                                                <span className="font-medium text-blue-700">Excel İndir:</span>
                                                <p className="text-blue-600 mt-1">Doğrudan Excel formatında, grafikler için ideal</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Önemli Notlar */}
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                                            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
                                            Önemli Notlar
                                        </h4>
                                        <ul className="text-red-800 text-sm space-y-1 list-disc list-inside">
                                            <li>Manuel düzeltmeler geri alınamaz, dikkatli olun</li>
                                            <li>Tüm değişiklikler audit log'da kayıt altına alınır</li>
                                            <li>Büyük miktarlarda değişiklik yapmadan önce yedek alın</li>
                                            <li>Müşteri ile iletişim kurduktan sonra düzeltme yapın</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminCampaignTrackingPanel;